/**
 * `window.PinksyGlobalState` — the host state every hosted module reads.
 * Forked from Metaship's `MetaShipGlobalState` (same method names and shapes,
 * D28) so the module recipe is unchanged; the Metaship-only keys (organisation
 * tokens, the persisted-Vuex mirror) are gone — Pinksy's modules are
 * greenfield and read this object only (build plan §7).
 *
 * `.state` MUST stay a directly-readable object that is REPLACED (not
 * mutated) on every update — modules read `window.PinksyGlobalState.state.<key>`
 * as a plain property as well as through `getState()`.
 */
import { DEFAULT_LOCALE } from "@/i18n/locales";
import type { NavBarItem } from "@/app/types";

/**
 * Who is signed in, as far as the shell needs to know: the Passport id and
 * what to draw beside it. Never a person record — the Passport is the person
 * (D1, D27); anything more comes through a grant, on the module's own request.
 */
export interface HostUser {
  userHash: string;
  displayName: string | null;
  avatar: string | null;
  /** The account handle — an e-mail where the record has one. What a person recognises themselves by. */
  handle?: string | null;
}

/**
 * The production the visitor is INSIDE (D32). The shell reads it off the URL (`?production=<uuid>`,
 * nav/productionScope.ts) and publishes it here, so a module reads the production exactly the way it
 * reads the user — `window.PinksyGlobalState.state.production` — instead of parsing the URL for
 * itself, and instead of asking the person a second time in its own dropdown.
 *
 * `title` arrives a moment after `id`: the id is on the URL, the title is fetched. Null means "not
 * known yet, or not knowable" — never a reason to draw a uuid at a person.
 */
export interface HostProduction {
  id: string;
  title: string | null;
}

/**
 * 🔴 **THE COMPANY THIS SESSION IS ACTING FOR** (D37, D41) — the switcher's answer, published so a
 * module reads it the way it reads the user, and never asks the person a second time in a dropdown of
 * its own.
 *
 * A module does NOT have to send it: the shell puts it on every request as `X-Organisation`
 * (`auth/http.ts`), and a module that uses the published token does the same. What a module needs it
 * FOR is drawing — "acting for Wilder Hearts" on a screen, or a heading — and for knowing that the
 * context changed underneath it, which arrives through the ordinary subscription.
 *
 * Null before a choice is possible: a session that has just started, or a person who belongs to no
 * live company. Every organisation-scoped endpoint refuses in that state, which is correct, and a
 * screen that says so is better than one that silently shows nothing.
 */
export interface HostOrganisation {
  /** The public identifier — what travels as `X-Organisation` (D39). */
  id: string;
  /** The trading name where there is one. What the person calls the company. */
  name: string;
  /** What this person IS to it (D22) — principal · employee · contractor · agent · booker. */
  relationship: string;
  /**
   * 🔴 **WHAT THIS COMPANY DOES** (D57) — `NETWORK_FUNCTIONS` ids, empty until somebody has said.
   *
   * Published for the same reason the relationship is: a module drawing for an agency may need to say
   * so, and asking the API a second question the shell already has the answer to is how two screens
   * start disagreeing about what a company is. Which modules a company is OFFERED is decided in the
   * shell (`nav/navFunctions.ts`), not here.
   */
  functions: string[];
}

/**
 * 🔴 An operator is driving this session AS somebody (D34). Published so the shell can wear a banner
 * for the whole of it and every module can see it without being told.
 *
 * It is on the STATE and not merely in the token because the point is that a person can SEE it. An
 * operator who forgets they are assisting is the way this feature turns into an incident, and a claim
 * nobody renders is a claim nobody notices.
 */
export interface HostAssisting {
  /** The operator really acting — the `act` claim of the session token. */
  operatorPassportId: string;
  /** Who is being assisted. `name` arrives a moment after the id, like a production's title. */
  subjectPassportId: string;
  subjectName: string | null;
  /** Why, in the operator's own words. Shown, because a reason nobody reads is not accountability. */
  reason: string;
  /** When the assisted session ends by itself, ms since the epoch. */
  until: number;
}

export interface HostState {
  darkMode: boolean;
  /** True once the visitor has used the theme toggle — only then does their
   *  choice outrank the tenant's default. */
  themeChosen?: boolean;
  /** BCP-47 tag of the active locale (src/i18n). */
  locale: string;
  /** The raw Passport session JWT; modules send it as `Authorization: Bearer <token>`. */
  token: string | null;
  /** Its `exp` claim in milliseconds since the epoch. */
  tokenExpiry: number | null;
  permissions: NavBarItem[] | null;
  currentRoute: string | null;
  user: HostUser | null;
  /** The production this screen is inside; null on the slate and everywhere that is not one production's. */
  production: HostProduction | null;
  /** The company this session is acting for; null before a choice is possible. Sent as `X-Organisation`. */
  organisation: HostOrganisation | null;
  /**
   * 🔴 True once `/me/organisations` has answered, so a module can tell "no company" from "not known yet" (D164a):
   * a person with nothing selectable lands on her work, and a staff member on a cold load must not land there
   * for the half second before their company arrives. Optional: absent reads as not settled.
   */
  organisationSettled?: boolean;
  /** Null on an ordinary session — which is nearly always. Set only while an operator is assisting (D34). */
  assisting: HostAssisting | null;
}

export interface PinksyGlobalStateApi {
  state: HostState;
  subscribers: Array<(state: HostState) => void>;
  getState(): HostState;
  setState(newState: HostState): void;
  updateState(partial: Partial<HostState>): void;
  subscribe(callback: (state: HostState) => void): () => void;
  notifySubscribers(): void;
  toggleTheme(): void;
  /**
   * A module hands the shell a session it obtained on its own — the claim module's link session (P-09, D24), the
   * one way a person with nothing arrives at a signed-in shell. The shell verifies the token against the Passport's
   * keys exactly as it verifies its own (auth/verify.ts), holds it for the tab, and goes live: chrome, navigation,
   * the token published here for every module. Resolves false, and changes nothing, when the token does not verify.
   * Bound by the boot (`bindSessionAdopter`); a host that never bound one answers false.
   */
  adoptSession(token: string): Promise<boolean>;
}

export type SessionAdopter = (token: string) => Promise<boolean>;

declare global {
  interface Window {
    PinksyGlobalState?: PinksyGlobalStateApi;
  }
}

export const GLOBAL_STATE_KEY = "pinksy-global-state";

export function defaultState(): HostState {
  return {
    darkMode: true,
    locale: DEFAULT_LOCALE,
    token: null,
    tokenExpiry: null,
    permissions: null,
    currentRoute: null,
    user: null,
    assisting: null,
    production: null,
    organisation: null,
  };
}

/**
 * `HostState` minus the credential, the production and the assist — the shape written to localStorage.
 *
 * 🔴 `assisting` is forced out for the same reason as the token: it describes a CREDENTIAL this tab
 * holds, and a remembered banner over an ordinary session would be a lie in the most alarming
 * possible direction.
 *
 * 🔴 So is the ORGANISATION, and for the reason the production is: this is `localStorage`, shared by
 * every tab of this browser, and a company remembered there would move a second tab under the first
 * one's feet. The choice is per TAB (`stores/organisation.ts`, sessionStorage) and is re-settled
 * against a freshly fetched list at every boot — so a standing that changed while the tab was closed
 * cannot come back as a context the gate then refuses on every request.
 */
export type PersistedHostState = Omit<HostState, "token" | "tokenExpiry" | "production" | "assisting" | "organisation">;

/**
 * 🔴 **A KEY THAT LEAVES THE CONTRACT MUST BE SWEPT OUT OF STORAGE TOO** (D118, PNK-121).
 *
 * `tenant` was on this state and was persisted. Nothing writes it any more — and nothing ever READ it
 * outside the kit's own tests — but `loadPersistedState()` spreads the stored blob over the defaults,
 * so a browser that had run the old shell went on publishing a tenant nothing refreshed: a dead
 * hostname and a pink migration 057 retired, on the object every module reads. Found in Chrome on the
 * boot that removed the writer, which is the only place it was ever visible.
 *
 * Listing the key here rather than trusting a type: `Partial<HostState>` cannot describe a field the
 * type no longer has, so TypeScript is silent about exactly this class of leftover.
 */
const RETIRED_STATE_KEYS = ["tenant"] as const;

/**
 * What survives a reload. The credential is deliberately NOT here: it lives
 * in sessionStorage (auth/session.ts), scoped to the tab, and is republished
 * onto the live state at boot — localStorage would keep it for every tab, for
 * good.
 *
 * 🔴 Neither is the PRODUCTION. It lives on the URL and is re-read from there on every navigation
 * (stores/production.ts), which is the whole point of putting it there: a remembered production would
 * outlive the link that chose it and quietly re-enter a tab that is on the slate — the same shape as
 * the bug where the next person at a shared machine read the last person's screen.
 */
export function toPersisted(state: HostState): PersistedHostState {
  return {
    darkMode: state.darkMode,
    themeChosen: state.themeChosen,
    locale: state.locale,
    permissions: state.permissions,
    currentRoute: state.currentRoute,
    user: state.user,
  };
}

function loadPersistedState(): HostState | null {
  try {
    const raw = localStorage.getItem(GLOBAL_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<HostState>;
    // `production` is forced out for the same reason as the token: whatever a stale blob holds, the
    // URL of THIS load decides which production this tab is inside — see toPersisted().
    const state = { ...defaultState(), ...parsed, token: null, tokenExpiry: null, production: null, assisting: null, organisation: null };
    for (const key of RETIRED_STATE_KEYS) delete (state as Record<string, unknown>)[key];
    return state;
  } catch {
    return null;
  }
}

function persist(state: HostState): void {
  try {
    localStorage.setItem(GLOBAL_STATE_KEY, JSON.stringify(toPersisted(state)));
  } catch {
    // best-effort — a full or blocked storage just loses the preferences
  }
}

/** Build the `PinksyGlobalState` singleton. Exported (not just installed) for tests. */
export function createGlobalState(): PinksyGlobalStateApi {
  const api: PinksyGlobalStateApi = {
    state: loadPersistedState() || defaultState(),
    subscribers: [],
    getState() {
      return api.state;
    },
    setState(newState) {
      api.state = { ...newState };
      persist(api.state);
      api.notifySubscribers();
    },
    updateState(partial) {
      api.state = { ...api.state, ...partial };
      persist(api.state);
      api.notifySubscribers();
    },
    subscribe(callback) {
      api.subscribers.push(callback);
      return () => {
        const i = api.subscribers.indexOf(callback);
        if (i > -1) api.subscribers.splice(i, 1);
      };
    },
    notifySubscribers() {
      api.subscribers.forEach((cb) => cb(api.state));
    },
    toggleTheme() {
      api.updateState({ darkMode: !api.state.darkMode, themeChosen: true });
      document.body.setAttribute("data-theme", api.state.darkMode ? "dark" : "light");
    },
    // Until the boot binds the session store's establishSession — nothing to adopt a session into.
    adoptSession: async () => false,
  };
  return api;
}

/** The boot gives the host its session adopter once the session store exists (stores/session.ts `establishSession`). */
export function bindSessionAdopter(api: PinksyGlobalStateApi, adopt: SessionAdopter): void {
  api.adoptSession = (token) => adopt(token);
}

/** Install the singleton on `window` and apply the initial theme attribute. */
export function installGlobalState(): PinksyGlobalStateApi {
  const api = createGlobalState();
  window.PinksyGlobalState = api;
  document.body.setAttribute("data-theme", api.state.darkMode ? "dark" : "light");
  return api;
}

/** True once anything has been persisted — i.e. this is not the browser's first visit. */
export function hasPersistedState(): boolean {
  try {
    return localStorage.getItem(GLOBAL_STATE_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * The TENANT's default theme applies until the visitor uses the theme toggle.
 * Keyed on an explicit `themeChosen`, not on "is anything persisted": a
 * merely-persisted `darkMode` (written by a session, never chosen) must not
 * outrank a tenant that asks to start light. Returns the theme now in force.
 */
export function applyTenantDefaultTheme(
  api: PinksyGlobalStateApi,
  defaultTheme: "light" | "dark" | null | undefined,
  fresh: boolean = !api.state.themeChosen,
): "light" | "dark" {
  if (fresh) {
    const darkMode = defaultTheme !== "light";
    if (darkMode !== api.state.darkMode) api.updateState({ darkMode });
  }
  const theme = api.state.darkMode ? "dark" : "light";
  document.body.setAttribute("data-theme", theme);
  return theme;
}

/**
 * 🔴 The module ASKS the shell to start assisting (D34); it never swaps the session itself. The token
 * is the Passport's answer to `POST /auth/assist`, and only the shell holds the credential this tab
 * signs in with — a module reaching into that would be a module that can sign anybody in.
 */
export const HOST_ASSIST_EVENT = "pinksy:assist";

export interface HostAssistRequest {
  /** The assisted session token the Passport minted. */
  token: string;
  /** Who is being assisted, for the banner — the shell verifies the token and reads the ids off it. */
  subjectName: string | null;
  /** The reason the operator gave, echoed by the endpoint. */
  reason: string;
}
